import { MessageSquareText } from 'lucide-react';
import { DetailPanel } from '@/components/common/DetailPanel';
import { NumberMark } from '@/components/common/NumberMark';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { SURVEY_QUESTION_TYPE_LABELS } from '../constants/survey.constants';
import type { SurveySubmissionAnswer } from '../types/survey.types';

const NO_ANSWER_TEXT = 'No answer was given.';
const SKELETON_ANSWER_KEYS = ['first', 'second', 'third'];
const ANSWER_BOX_CLASS = 'rounded-xl bg-primary/4 px-4 py-3';

function AnswerSkeleton() {
  return (
    <li className="flex gap-3">
      <Skeleton className="size-6 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="h-4 w-3/5" />
        <div className={cn(ANSWER_BOX_CLASS, 'flex flex-col gap-2')}>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </li>
  );
}

interface AnswerItemProps {
  answer: SurveySubmissionAnswer;
  position: number;
}

function AnswerItem({ answer, position }: AnswerItemProps) {
  return (
    <li className="flex gap-3">
      <NumberMark value={position} />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-sm font-medium wrap-break-word">
          {answer.questionText}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {SURVEY_QUESTION_TYPE_LABELS[answer.questionType]}
          </span>
        </p>
        <div
          className={cn(
            ANSWER_BOX_CLASS,
            'text-sm leading-relaxed wrap-break-word whitespace-pre-line',
          )}
        >
          {answer.answerText ?? <span className="text-muted-foreground">{NO_ANSWER_TEXT}</span>}
        </div>
      </div>
    </li>
  );
}

interface SurveySubmissionAnswersPanelProps {
  answers: SurveySubmissionAnswer[] | undefined;
}

export function SurveySubmissionAnswersPanel({ answers }: SurveySubmissionAnswersPanelProps) {
  return (
    <DetailPanel title="Answers" icon={MessageSquareText}>
      <ol className="flex flex-col gap-5">
        {answers === undefined
          ? SKELETON_ANSWER_KEYS.map((key) => <AnswerSkeleton key={key} />)
          : answers.map((answer, index) => (
              <AnswerItem key={answer.questionId} answer={answer} position={index + 1} />
            ))}
      </ol>
    </DetailPanel>
  );
}
